import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card,
  CardBody,
  Row,
  Col,
  Badge,
  UncontrolledTooltip,
} from 'reactstrap';
import IconButton from '~/components/IconButton';

/**
 * Labels dos eventos suportados pelo webhook. Chave = valor.
 * Mantido explícito pra facilitar customização futura (ex: traduzir
 * label ou agrupar por categoria na UI).
 *
 * Se um evento novo for adicionado no backend mas não aqui, ele ainda
 * renderiza via fallback `EVENT_LABEL[ev] || ev`.
 */
const EVENT_LABEL = {
  'cart.created': 'cart.created',
  'cart.in_fraud_analysis': 'cart.in_fraud_analysis',
  'cart.paid': 'cart.paid',
  'cart.in_delivery': 'cart.in_delivery',
  'cart.completed': 'cart.completed',
  'cart.cancelled': 'cart.cancelled',
  'cart.payment_rejected': 'cart.payment_rejected',
  'cart.fraud_rejected': 'cart.fraud_rejected',
  'cart.awaiting_refund': 'cart.awaiting_refund',
  'cart.refunded': 'cart.refunded',
  'cart.chargeback': 'cart.chargeback',
  'cart.returned': 'cart.returned',
  'delivery.status_changed': 'delivery.status_changed',
};

const ENV_LABEL = {
  test: { label: 'TESTE', color: '#6b7280' },
  live: { label: 'PROD', color: '#2544b1' },
};

/**
 * Máximo de badges de eventos exibidos inline antes de agrupar o
 * restante em "+N mais". Escolhido pra caber confortavelmente na
 * coluna de eventos sem quebrar layout mesmo com nomes longos.
 */
const MAX_VISIBLE_EVENTS = 3;

const httpFailureColor = count => {
  if (count >= 10) return '#ef4444';
  if (count >= 5) return '#f59e0b';
  return 'inherit';
};

const statusBadge = webhook => {
  if (webhook.auto_disabled_at) {
    return { label: 'Auto-desativado', bg: '#ef4444' };
  }
  if (!webhook.enabled) {
    return { label: 'Desativado', bg: '#9ca3af' };
  }
  return { label: 'Ativo', bg: '#10b981' };
};

/**
 * Estilo compartilhado dos badges de evento (desktop e mobile).
 * `fontSize` é passado dinamicamente porque mobile usa 9px vs 10px do desktop.
 */
const eventBadgeStyle = fontSize => ({
  backgroundColor: '#e0e7ff',
  color: '#3730a3',
  fontSize,
  padding: fontSize === '9px' ? '2px 5px' : '3px 6px',
  fontFamily: '"JetBrains Mono", Menlo, Monaco, Consolas, monospace',
});

/**
 * Estilo do badge "+N mais" — mesmo visual dos eventos mas cor
 * ligeiramente diferente pra sinalizar que é ação (hover).
 */
const overflowBadgeStyle = fontSize => ({
  backgroundColor: '#c7d2fe',
  color: '#1e1b4b',
  fontSize,
  padding: fontSize === '9px' ? '2px 5px' : '3px 6px',
  fontFamily: '"JetBrains Mono", Menlo, Monaco, Consolas, monospace',
  cursor: 'default',
});

/**
 * Renderiza a lista de badges de eventos com truncamento inteligente:
 *   - Exibe os primeiros MAX_VISIBLE_EVENTS badges normalmente
 *   - Se houver mais, adiciona um badge "+N mais" com tooltip listando
 *     todos os eventos escondidos
 *
 * O tooltip usa UncontrolledTooltip com id único baseado em webhookId +
 * variant (desktop/mobile) pra evitar colisão quando o mesmo webhook
 * é renderizado em ambos os layouts (embora só um esteja visível por vez
 * via display responsive).
 */
const EventBadges = ({ events, webhookId, variant = 'desktop' }) => {
  const eventList = events || [];
  const fontSize = variant === 'mobile' ? '9px' : '10px';
  const visibleEvents = eventList.slice(0, MAX_VISIBLE_EVENTS);
  const hiddenEvents = eventList.slice(MAX_VISIBLE_EVENTS);
  const hasOverflow = hiddenEvents.length > 0;

  const overflowId = `events-overflow-${variant}-${webhookId}`;

  return (
    <>
      {visibleEvents.map(ev => (
        <Badge
          key={ev}
          className={variant === 'mobile' ? 'mr-1' : ''}
          style={eventBadgeStyle(fontSize)}
        >
          {EVENT_LABEL[ev] || ev}
        </Badge>
      ))}

      {hasOverflow && (
        <>
          <Badge
            id={overflowId}
            className={variant === 'mobile' ? 'mr-1' : ''}
            style={overflowBadgeStyle(fontSize)}
          >
            +{hiddenEvents.length} mais
          </Badge>
          <UncontrolledTooltip
            target={overflowId}
            placement="top"
            autohide={false}
          >
            {hiddenEvents.map(ev => (
              <div
                key={ev}
                style={{
                  fontFamily:
                    '"JetBrains Mono", Menlo, Monaco, Consolas, monospace',
                  fontSize: '11px',
                  padding: '1px 0',
                  textAlign: 'left',
                }}
              >
                {EVENT_LABEL[ev] || ev}
              </div>
            ))}
          </UncontrolledTooltip>
        </>
      )}
    </>
  );
};

export default function PartnerWebhookCard({
  webhook,
  onReactivate,
  onDelete,
}) {
  const history = useHistory();
  const status = statusBadge(webhook);
  const env =
    ENV_LABEL[webhook?.partner_application?.environment] || ENV_LABEL.test;
  const canReactivate = Boolean(webhook.auto_disabled_at);

  const handleViewDispatches = () => {
    history.push(`/webhooks-dispatches?partner_webhook_id=${webhook.id}`);
  };

  const handleEdit = () => {
    history.push(`/webhooks-parceiros/${webhook.id}/editar`);
  };

  return (
    <Card
      style={{
        boxShadow: '0rem 0.275rem 0.3rem 0.1rem rgba(0, 0, 0, 0.035)',
      }}
      className="card new-layout list-card overflow-hidden"
    >
      <CardBody className="card-body new-layout px-3 py-3">
        {/* desktop */}
        <Row className="w-100 h-100 mx-0 d-none d-lg-flex align-items-center">
          <Col xs={3} align="start">
            <div className="new-layout-card-list-strong-value text-truncate">
              {webhook?.partner_application?.name || (
                <span className="text-muted">—</span>
              )}
            </div>
            <Badge
              style={{
                backgroundColor: env.color,
                color: '#fff',
                fontSize: '9px',
                padding: '2px 6px',
                marginTop: 2,
              }}
            >
              {env.label}
            </Badge>
          </Col>

          <Col xs={3} align="start">
            <div
              className="text-truncate"
              id={`url-tooltip-${webhook.id}`}
              style={{
                fontFamily:
                  '"JetBrains Mono", Menlo, Monaco, Consolas, monospace',
                fontSize: '12px',
              }}
            >
              {webhook.url}
            </div>
            <UncontrolledTooltip target={`url-tooltip-${webhook.id}`}>
              {webhook.url}
            </UncontrolledTooltip>
            {webhook.description && (
              <div
                className="text-muted text-truncate"
                style={{ fontSize: '11px' }}
              >
                {webhook.description}
              </div>
            )}
          </Col>

          <Col xs={2} align="start">
            <div className="d-flex flex-row flex-wrap" style={{ gap: 4 }}>
              <EventBadges
                events={webhook.events}
                webhookId={webhook.id}
                variant="desktop"
              />
            </div>
          </Col>

          <Col xs={1} align="start">
            <Badge
              style={{
                backgroundColor: status.bg,
                color: '#fff',
                fontSize: '10px',
                padding: '4px 8px',
              }}
            >
              {status.label}
            </Badge>
          </Col>

          <Col xs={1} align="start">
            <div className="new-layout-table-column-value">
              <span
                style={{
                  color: httpFailureColor(webhook.consecutive_failures || 0),
                  fontWeight: webhook.consecutive_failures > 0 ? 600 : 400,
                }}
              >
                {webhook.consecutive_failures || 0}
              </span>
            </div>
          </Col>

          <Col xs={1} align="start">
            <div
              className="new-layout-table-column-value"
              style={{ fontSize: '12px' }}
            >
              {webhook.formatted_created_at}
            </div>
          </Col>

          <Col xs={1} align="center" className="h-100 px-2">
            <div className="h-100 d-flex flex-row align-items-center justify-content-around">
              <IconButton
                icon="las la-list"
                color="info"
                id={`dispatches-btn-${webhook.id}`}
                tip="Ver dispatches"
                forceTip
                click={handleViewDispatches}
              />
              <IconButton
                icon="las la-edit"
                color="primary"
                id={`edit-btn-${webhook.id}`}
                tip="Editar"
                forceTip
                click={handleEdit}
              />
              {canReactivate && (
                <IconButton
                  icon="las la-undo"
                  color="success"
                  id={`reactivate-btn-${webhook.id}`}
                  tip="Reativar"
                  forceTip
                  click={() => onReactivate(webhook)}
                />
              )}
              <IconButton
                icon="las la-trash"
                color="danger"
                id={`delete-btn-${webhook.id}`}
                tip="Excluir"
                forceTip
                click={() => onDelete(webhook)}
              />
            </div>
          </Col>
        </Row>

        {/* mobile */}
        <Row className="w-100 h-100 mx-0 d-lg-none">
          <Col className="px-0">
            <Row className="w-100 mx-0">
              <Col xs={8} align="start" className="px-0">
                <div className="new-layout-card-mobile font-weight-600">
                  {webhook?.partner_application?.name || '—'}
                </div>
              </Col>
              <Col xs={4} className="px-0 d-flex justify-content-end">
                <IconButton
                  icon="las la-list"
                  color="info"
                  id={`dispatches-m-${webhook.id}`}
                  tip="Ver dispatches"
                  click={handleViewDispatches}
                />
                <IconButton
                  icon="las la-edit"
                  color="primary"
                  id={`edit-m-${webhook.id}`}
                  tip="Editar"
                  click={handleEdit}
                />
                {canReactivate && (
                  <IconButton
                    icon="las la-undo"
                    color="success"
                    id={`reactivate-m-${webhook.id}`}
                    tip="Reativar"
                    click={() => onReactivate(webhook)}
                  />
                )}
                <IconButton
                  icon="las la-trash"
                  color="danger"
                  id={`delete-m-${webhook.id}`}
                  tip="Excluir"
                  click={() => onDelete(webhook)}
                />
              </Col>
            </Row>
            <hr />
            <Row className="w-100 mx-0 mb-2">
              <Badge
                className="mr-2"
                style={{
                  backgroundColor: env.color,
                  color: '#fff',
                  fontSize: '10px',
                  padding: '3px 6px',
                }}
              >
                {env.label}
              </Badge>
              <Badge
                style={{
                  backgroundColor: status.bg,
                  color: '#fff',
                  fontSize: '10px',
                  padding: '3px 6px',
                }}
              >
                {status.label}
              </Badge>
            </Row>
            <Row className="w-100 mx-0">
              <Col className="px-0">
                <div className="d-flex flex-row align-items-baseline">
                  <div className="new-layout-card-mobile label font-weight-500 d-inline">
                    URL:
                  </div>
                  <div
                    className="new-layout-card-mobile value d-inline text-truncate ml-2"
                    style={{
                      fontFamily: '"JetBrains Mono", Menlo, monospace',
                      fontSize: '11px',
                    }}
                  >
                    {webhook.url}
                  </div>
                </div>
              </Col>
            </Row>
            <Row className="w-100 mx-0">
              <Col className="px-0">
                <div className="d-flex flex-row align-items-baseline flex-wrap">
                  <div className="new-layout-card-mobile label font-weight-500 d-inline mr-2">
                    Eventos:
                  </div>
                  <EventBadges
                    events={webhook.events}
                    webhookId={webhook.id}
                    variant="mobile"
                  />
                </div>
              </Col>
            </Row>
            <Row className="w-100 mx-0">
              <Col className="px-0">
                <div className="d-flex flex-row align-items-baseline">
                  <div className="new-layout-card-mobile label font-weight-500 d-inline">
                    Falhas consecutivas:
                  </div>
                  <div className="new-layout-card-mobile value d-inline ml-2">
                    {webhook.consecutive_failures || 0}
                  </div>
                </div>
              </Col>
            </Row>
            <Row className="w-100 mx-0">
              <Col className="px-0">
                <div className="d-flex flex-row align-items-baseline">
                  <div className="new-layout-card-mobile label font-weight-500 d-inline">
                    Criado:
                  </div>
                  <div className="new-layout-card-mobile value d-inline ml-2">
                    {webhook.formatted_created_at}
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
}
